const lightPresentation = new WeakMap();

function copyPresentation(source, shape) {
  return Object.fromEntries(
    Object.entries(shape).map(([key, value]) => [
      key,
      value && typeof value === 'object'
        ? copyPresentation(source?.[key], value)
        : (source?.[key] ?? null),
    ]),
  );
}

function captureLight(chart) {
  const { xAxis, yAxis, ...shape } = getChartTheme(chart.renderTo);
  return {
    presentation: copyPresentation(chart.options, shape),
    xAxis: chart.xAxis.map((axis) => copyPresentation(axis.options, xAxis)),
    yAxis: chart.yAxis.map((axis) => copyPresentation(axis.options, yAxis)),
  };
}

export function getChartTheme(element) {
  const css = getComputedStyle(element);
  const value = (name) => css.getPropertyValue(name).trim();
  const text = value('--chart-text');
  const axisText = value('--chart-axis-text') || text;
  const grid = value('--chart-grid');
  return {
    chart: { backgroundColor: value('--chart-background') },
    title: { style: { color: text } },
    subtitle: { style: { color: text } },
    legend: { itemStyle: { color: text }, itemHoverStyle: { color: text } },
    xAxis: {
      lineColor: grid,
      tickColor: grid,
      gridLineColor: grid,
      labels: { style: { color: axisText } },
      title: { style: { color: text } },
    },
    yAxis: {
      lineColor: grid,
      tickColor: grid,
      gridLineColor: grid,
      labels: { style: { color: axisText } },
      title: { style: { color: text } },
    },
    tooltip: {
      backgroundColor: value('--chart-tooltip-background'),
      borderColor: grid,
      style: { color: text },
    },
    credits: { style: { color: text } },
  };
}

export function applyChartTheme(chart) {
  if (!chart?.renderTo || !chart.update) return;
  const isDark = document.body.dataset.theme === 'dark';
  if (!isDark && !lightPresentation.has(chart)) return;
  if (!lightPresentation.has(chart)) lightPresentation.set(chart, captureLight(chart));
  if (!isDark) {
    const original = lightPresentation.get(chart);
    chart.update(original.presentation, false);
    chart.xAxis.forEach((axis, index) => axis.update(original.xAxis[index], false));
    chart.yAxis.forEach((axis, index) => axis.update(original.yAxis[index], false));
    chart.redraw(false);
    return;
  }
  const options = getChartTheme(chart.renderTo);
  const { xAxis, yAxis, ...rest } = options;
  chart.update(rest, false);
  chart.xAxis.forEach((axis) => axis.update(xAxis, false));
  chart.yAxis.forEach((axis) => axis.update(yAxis, false));
  chart.redraw(false);
}

function parseHex(color) {
  if (typeof color !== 'string') return null;
  const match = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  const hex = match[1].length === 3 ? match[1].replace(/./g, (c) => c + c) : match[1];
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function toHsl([r, g, b]) {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h / 6, s, l];
}

function toRgb([h, s, l]) {
  if (s === 0) return [l, l, l].map((v) => Math.round(v * 255));
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t) => {
    const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [h + 1 / 3, h, h - 1 / 3].map((t) => Math.round(channel(t) * 255));
}

export function liftLineColor(color, background, minRatio = 3) {
  const rgb = parseHex(color);
  const bg = parseHex(background);
  if (!rgb || !bg || contrast(rgb, bg) >= minRatio) return color;
  const [h, s, startL] = toHsl(rgb);
  let lifted = rgb;
  for (let l = startL; l <= 0.9 && contrast(lifted, bg) < minRatio; l += 0.05) {
    lifted = toRgb([h, s, Math.min(l + 0.05, 0.9)]);
  }
  return `#${lifted.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export function readableLineColor(color, theme = document.body.dataset.theme) {
  if (theme !== 'dark') return color;
  const background = getComputedStyle(document.body).getPropertyValue('--chart-background').trim();
  return liftLineColor(color, background);
}

export function candleLineColors(upColor, theme = document.body.dataset.theme) {
  if (theme !== 'dark') return {};
  const lineColor = getComputedStyle(document.body).getPropertyValue('--chart-text').trim();
  return { lineColor, upLineColor: upColor };
}
